import React, { useState } from 'react';
import { ChatRoom } from '@/types/message';
import { 
  CheckCircle2, 
  Link2, 
  Info, 
  ArrowRight, 
  Sparkles, 
  ShieldCheck,
  Check
} from 'lucide-react';

interface FeaturedRoomsProps {
  rooms: ChatRoom[];
  onJoinRoom: (room: ChatRoom) => void;
}

export const FeaturedRooms: React.FC<FeaturedRoomsProps> = ({ rooms, onJoinRoom }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedInfoRoom, setSelectedInfoRoom] = useState<ChatRoom | null>(null);

  const handleCopyLink = (room: ChatRoom, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/messages?roomId=${room.slug}`;
    navigator.clipboard.writeText(url);
    setCopiedId(room.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleShowInfo = (room: ChatRoom, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedInfoRoom(room);
  };

  return (
    <div className="flex-1 overflow-y-auto min-h-0 p-4 md:p-8 bg-background">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header (y99.in style) */}
        <div className="text-center md:text-left space-y-1.5 pb-2 border-b border-border/40">
          <div className="flex items-center justify-center md:justify-start gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-xl md:text-2xl font-bold tracking-tight text-foreground">
              Featured Chat Rooms
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Connect with verified buyers and sellers, discover trending deals, or get instant help in our open community lounges.
          </p>
        </div>

        {/* Room Cards List */}
        <div className="grid gap-4">
          {rooms.map((room) => {
            const isWelcomeRoom = room.slug === 'welcome-room' || room.id === 'room-welcome-system';

            return (
              <div
                key={room.id}
                onClick={() => onJoinRoom(room)}
                className={`group relative rounded-2xl p-5 md:p-6 transition-all duration-200 cursor-pointer border ${
                  isWelcomeRoom
                    ? 'bg-gradient-to-br from-primary/[0.04] via-card to-card border-primary/30 shadow-md shadow-primary/5 hover:border-primary hover:shadow-lg'
                    : 'bg-card border-border/60 hover:border-border hover:shadow-sm'
                }`}
              >
                {/* Top Row: Title + Verified Badge + Online Count */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl shrink-0 shadow-inner ${
                      isWelcomeRoom 
                        ? 'bg-primary/10 text-primary border border-primary/20' 
                        : 'bg-muted text-foreground'
                    }`}>
                      {room.icon || '💬'}
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base md:text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                          {room.name}
                        </h3>

                        {room.verified && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                            <CheckCircle2 className="h-3 w-3 fill-blue-500/20" />
                            Verified
                          </span>
                        )}

                        {room.badge && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground">
                            {room.badge}
                          </span>
                        )}
                      </div>

                      <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                        {room.description}
                      </p>
                    </div>
                  </div>

                  {/* Online Badge */}
                  <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 shadow-xs">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>{room.onlineCount ?? 0} Online</span>
                  </div>
                </div>

                {/* Topic Quote Banner (y99.in signature feature) */}
                {room.topic && (
                  <div className="mt-4 bg-muted/60 dark:bg-muted/30 border-l-4 border-primary/70 rounded-r-lg px-3.5 py-2 text-xs text-muted-foreground flex items-center gap-2">
                    <span className="font-bold text-foreground shrink-0">Topic:</span>
                    <span className="truncate">{room.topic}</span>
                  </div>
                )}

                {/* Bottom Action Bar */}
                <div className="mt-4 pt-3 border-t border-border/40 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onJoinRoom(room);
                      }}
                      className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs md:text-sm font-semibold px-4 py-2 rounded-full transition-all duration-150 active:scale-95 shadow-sm shadow-primary/20"
                    >
                      <span>JOIN CHAT</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Action Icons: Copy link & Info */}
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <button
                      type="button"
                      title={copiedId === room.id ? 'Copied!' : 'Copy Room Link'}
                      onClick={(e) => handleCopyLink(room, e)}
                      className="p-2 rounded-full hover:bg-muted hover:text-foreground transition-colors"
                    >
                      {copiedId === room.id ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <Link2 className="h-4 w-4" />
                      )}
                    </button>

                    <button
                      type="button"
                      title="Room Guidelines & Info"
                      onClick={(e) => handleShowInfo(room, e)}
                      className="p-2 rounded-full hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Community Guidelines Banner */}
        <div className="p-4 rounded-xl bg-muted/40 border border-border/50 text-xs text-muted-foreground flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
          <p>
            <strong className="text-foreground">Safe Community Guidelines:</strong> Be respectful to other members. Never share passwords, OTPs, or financial secrets in public chat rooms.
          </p>
        </div>
      </div>

      {/* Info Modal */}
      {selectedInfoRoom && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-xs"
          onClick={() => setSelectedInfoRoom(null)}
        >
          <div 
            className="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{selectedInfoRoom.icon}</span>
                <h3 className="text-lg font-bold text-foreground">{selectedInfoRoom.name}</h3>
              </div>
              <button 
                onClick={() => setSelectedInfoRoom(null)}
                className="text-muted-foreground hover:text-foreground text-sm font-semibold p-1"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-muted-foreground">
              {selectedInfoRoom.description}
            </p>

            <div className="p-3 bg-muted rounded-xl text-xs space-y-1.5">
              <p className="font-semibold text-foreground">Room Guidelines:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Zero tolerance for spam or abusive language</li>
                <li>Post free ads directly via the Post Ad button</li>
                <li>Report any scam attempts to support@memads.in</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setSelectedInfoRoom(null)}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const r = selectedInfoRoom;
                  setSelectedInfoRoom(null);
                  onJoinRoom(r);
                }}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Join Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
